// src/layouts/AdminLayout.tsx
import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  CssBaseline,
  Button,
  Menu,
  MenuItem,
  Divider,
  useMediaQuery,
  Paper,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  Home as HomeIcon,
  People as PeopleIcon,
  ReceiptLong as ReceiptLongIcon,
  Inventory as InventoryIcon,
  Settings as SettingsIcon,
  PointOfSale as PosIcon,
  ListAlt as TicketsIcon,
  AccountBalance as AccountsIcon,
  LocationOn as LocationIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  ArrowDropDown as ChevronIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Gavel as TermsIcon,
  PrivacyTip as PrivacyIcon,
  HelpOutline as HelpIcon,
  AdminPanelSettings as SuperAdminIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";
import repair360Logo from "../logo 3.png";
import ThemeToggle from "../components/ThemeToggle";

type AdminLayoutProps = { children?: ReactNode };

type SubItem = {
  label: string;
  to: string;
  external?: boolean;
  disabled?: boolean;
};
type MenuItemConf = {
  key: string;
  label: string;
  icon: React.ReactNode;
  to?: string;
  children?: SubItem[];
};

export default function AdminLayout({ children }: AdminLayoutProps) {
  const { token, role, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const isXs = useMediaQuery("(max-width:600px)");
  const isSmDown = useMediaQuery("(max-width:900px)");

  const [shop, setShop] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [hideSubBanner, setHideSubBanner] = useState(false);

  // Heights
  const NAVBAR_HEIGHT = isXs ? 96 : 128;  // tall nav to showcase big logo
  const FOOTER_HEIGHT = isXs ? 64 : 72;   // compact single-row footer
  const headerLogoHeight = Math.max(44, NAVBAR_HEIGHT - (isXs ? 18 : 24));

  // Footer quick links (internal routes)
  const footerLinks = [
    { label: "Terms", to: "/terms", icon: <TermsIcon sx={{ fontSize: 18 }} /> },
    { label: "Privacy", to: "/privacy", icon: <PrivacyIcon sx={{ fontSize: 18 }} /> },
    { label: "Help", to: "/help", icon: <HelpIcon sx={{ fontSize: 18 }} /> },
  ];

  useEffect(() => {
    const fetchShop = async () => {
      try {
        setError(null);
        const res = await api.get("/shops/me", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setShop(res.data);
        // If industry missing or legacy, prompt to choose
        const ik = res?.data?.industryKey;
        const legacy = ik === 'computer';
        if ((!ik || legacy) && !["/choose-industry"].includes(location.pathname)) {
          navigate('/choose-industry');
        }
      } catch (e: any) {
        setShop(null);
        setError(
          e?.response?.data?.message ||
            (e?.response?.status === 401
              ? "Not authenticated. Please login."
              : "Failed to load shop info")
        );
      }
    };
    if (token) fetchShop();
  }, [token, location.pathname, navigate]);

  // Subscription banner dismiss key by period end date
  useEffect(() => {
    const end = shop?.subscription?.currentPeriodEnd ? String(new Date(shop.subscription.currentPeriodEnd).getTime()) : '';
    const key = end ? `subBanner.dismissed.until.${end}` : '';
    if (!key) { setHideSubBanner(false); return; }
    const dismissed = localStorage.getItem(key) === '1';
    setHideSubBanner(dismissed);
  }, [shop?.subscription?.currentPeriodEnd]);

  const dismissSubBanner = () => {
    const end = shop?.subscription?.currentPeriodEnd ? String(new Date(shop.subscription.currentPeriodEnd).getTime()) : '';
    if (end) localStorage.setItem(`subBanner.dismissed.until.${end}`, '1');
    setHideSubBanner(true);
  };

  const daysUntil = (d?: any) => {
    if (!d) return null;
    const end = new Date(d).getTime();
    const now = Date.now();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const handleLogout = () => {
    logout();
    navigate("/landing");
  };

  const menus: MenuItemConf[] = useMemo(() => {
    const industryKey = shop?.industryKey || 'electronics';
    const ticketLabel = industryKey === 'auto' ? 'Work Orders' : 'Tickets';
    const items: MenuItemConf[] = [
      { key: "dashboard", label: "Dashboard", icon: <HomeIcon />, to: "/" },
      {
        key: "clients",
        label: "Customers",
        icon: <PeopleIcon />,
        to: "/clients",
        children: [
          { label: "List Customers", to: "/clients" },
          { label: "Add Customer", to: "/clients/add" },
        ],
      },
      {
        key: "tickets",
        label: ticketLabel,
        icon: <TicketsIcon />,
        to: "/tickets",
        children: [
          { label: industryKey === 'auto' ? 'Manage Work Orders' : 'Manage Tickets', to: "/tickets" },
          { label: industryKey === 'auto' ? 'Create Work Order' : 'Create Ticket', to: "/tickets/create" },
          { label: "Manage Categories", to: "/manage-categories" },
        ],
      },
      {
        key: "invoices",
        label: "Invoices",
        icon: <ReceiptLongIcon />,
        to: "/invoices",
        children: [
          { label: "List Invoices", to: "/invoices" },
          { label: "Create Invoice", to: "/invoices/create" },
        ],
      },
      {
        key: "pos",
        label: "POS",
        icon: <PosIcon />,
        to: "/pos",
        children: [
          { label: "Dashboard", to: "/pos" },
          { label: "New Sale", to: "/pos/new" },
          { label: "Reports", to: "/pos/reports" },
        ],
      },
      {
        key: "inventory",
        label: "Inventory",
        icon: <InventoryIcon />,
        to: "/inventory",
        children: [
          { label: "Inventory List", to: "/inventory" },
          { label: "Add Item", to: "/inventory/add" },
          { label: "Suppliers", to: "/suppliers" },
          { label: "Add Supplier", to: "/suppliers/add" },
          { label: "Purchase Orders", to: "/purchase-orders" },
          { label: "Create PO", to: "/purchase-orders/create" },
        ],
      },
      {
        key: "accounts",
        label: "Accounts",
        icon: <AccountsIcon />,
        to: "/accounts",
        children: [
          { label: "Dashboard", to: "/accounts" },
          { label: "Journals", to: "/accounts/journals" },
          { label: "Create Journal", to: "/accounts/journals/create" },
          { label: "Purchases", to: "/accounts/purchases" },
        ],
      },
    ];
    if (role === "ADMIN" || role === "MANAGER" || role === 'SUPERADMIN') {
          items.push({
            key: "settings",
            label: "Settings",
            icon: <SettingsIcon />,
            to: "/settings",
            children: [
              { label: "General", to: "/settings/general" },
              { label: "Branding & Templates", to: "/settings/branding" },
              { label: "Notifications", to: "/settings/notifications" },
              { label: "Manage Subscription", to: "/settings/subscription" },
              { label: "Users", to: "/settings/users" },
            ],
          });
        }
    if (role === 'SUPERADMIN') {
      items.unshift({ key: 'control', label: 'Super Admin', icon: <SuperAdminIcon />, to: '/control' });
    }
    // If industry provides menu hints, filter accordingly (fallback: show all)
    // We fetch summary lazily in a side-effect below and store allowed keys in state
    return items;
  }, [role, shop?.industryKey]);

  // Optional: fetch industry menu hints and filter
  const [allowedMenus, setAllowedMenus] = useState<string[] | null>(null);
  useEffect(() => {
    const load = async () => {
      try {
        if (!token || !shop?.industryKey) return;
        const { getIndustrySummary } = await import("../services/catalog");
        const summary = await getIndustrySummary(shop.industryKey, token);
        setAllowedMenus(summary.menus || null);
      } catch {
        setAllowedMenus(null);
      }
    };
    load();
  }, [token, shop?.industryKey]);
  // Derive role-based visibility without mutating menus
  const visibleMenus: MenuItemConf[] = useMemo(() => {
    const can = (roles: string[]) => (role === 'SUPERADMIN') || roles.includes(role || "");
    const rules: Record<string, string[]> = {
      "Add Item": ["ADMIN", "MANAGER"],
      "Suppliers": ["ADMIN", "MANAGER"],
      "Add Supplier": ["ADMIN", "MANAGER"],
      "Purchase Orders": ["ADMIN", "MANAGER"],
      "Create PO": ["ADMIN", "MANAGER"],
      "Manage Categories": ["ADMIN", "MANAGER"],
      "Create Invoice": ["ADMIN", "MANAGER", "CASHIER"],
      "Reports": ["ADMIN", "MANAGER"],
    };
    const allowedTop = (m: MenuItemConf) => {
      if (m.key === "accounts") return can(["ADMIN", "MANAGER"]);
      if (m.key === "settings") return can(["ADMIN", "MANAGER"]);
      if (m.key === "pos") return can(["ADMIN", "MANAGER", "CASHIER"]);
      if (m.key === "invoices") return can(["ADMIN", "MANAGER", "CASHIER", "TECHNICIAN"]);
      return true;
    };
    const filterChildren = (m: MenuItemConf): MenuItemConf => ({
      ...m,
      children: (m.children || []).filter((c) => {
        const r = rules[c.label];
        return r ? can(r) : true;
      }),
    });
    let out = (menus || []).map(filterChildren).filter(allowedTop);
    if (allowedMenus && allowedMenus.length) {
      out = out.filter((m) => m.key === "control" || allowedMenus.includes(m.key));
    }
    return out;
  }, [menus, role, allowedMenus]);

  // Desktop dropdowns
  const [anchorEls, setAnchorEls] = useState<Record<string, HTMLElement | null>>({});
  const openMenu = (key: string, el: HTMLElement) =>
    setAnchorEls((prev) => ({ ...prev, [key]: el }));
  const closeMenu = (key: string) =>
    setAnchorEls((prev) => ({ ...prev, [key]: null }));

  // Small-screen hamburger
  const [smallNavEl, setSmallNavEl] = useState<null | HTMLElement>(null);
  const openSmallNav = (el: HTMLElement) => setSmallNavEl(el);
  const closeSmallNav = () => setSmallNavEl(null);

  const isActive = (to?: string) =>
    to ? location.pathname === to || location.pathname.startsWith(to) : false;
  const go = (to?: string) => to && navigate(to);

  const renderIcon = useCallback((icon: React.ReactNode, size: number) => {
    if (!icon) return null;
    if (!React.isValidElement(icon)) return icon;
    const existingSx = (icon.props as any)?.sx;
    const mergedSx = Array.isArray(existingSx)
      ? [...existingSx, { fontSize: size }]
      : existingSx
      ? [existingSx, { fontSize: size }]
      : { fontSize: size };
    return React.cloneElement(icon, { sx: mergedSx } as any);
  }, []);

  const isDark = theme.palette.mode === 'dark';

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", flexDirection: "column" }}>
      <CssBaseline />

      {/* TOP APP BAR (nav only; big logo; no shop name) */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.appBar + 2,
          background: `linear-gradient(180deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          height: NAVBAR_HEIGHT,
          justifyContent: "flex-end",
          boxShadow: "0 4px 24px rgba(0,0,0,0.25)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Toolbar
          sx={{
            minHeight: NAVBAR_HEIGHT,
            alignItems: "flex-end",
            pb: 1.5,
            gap: 1,
            overflowX: "auto",
          }}
        >
          {/* Big app logo */}
          <Box
            component="button"
            type="button"
            onClick={() => go("/")}
            title="Go to dashboard"
            sx={{
              display: "flex",
              alignItems: "flex-end",
              gap: 1.25,
              pr: 2,
              mr: 1,
              borderRight: "1px solid rgba(255,255,255,0.15)",
              flex: "0 0 auto",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              p: 0,
            }}
          >
            <img
              src={repair360Logo}
              alt="RepairSync"
              style={{ height: headerLogoHeight, objectFit: "contain" }}
            />
          </Box>

          {/* Desktop / tablet nav buttons */}
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              alignItems: "flex-end",
              gap: 1,
            }}
          >
            {visibleMenus.map((m) => {
              const open = Boolean(anchorEls[m.key]);
              const active = isActive(m.to);
              const hasChildren = (m.children?.length || 0) > 0;

              return (
                <Box key={m.key} sx={{ flex: "0 0 auto", display: "flex", alignItems: "stretch" }}>
                  <Button
                    variant={active ? "contained" : "text"}
                    color={active ? "primary" : "inherit"}
                    onClick={() => m.to && go(m.to)}
                    disabled={!m.to}
                    sx={{
                      color: theme.palette.common.white,
                      textTransform: "none",
                      fontWeight: 900,
                      fontSize: isSmDown ? "1.05rem" : "1.18rem",
                      px: 2.7,
                      py: 1.7,
                      borderRadius: hasChildren ? "999px 0 0 999px" : 2,
                      position: "relative",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      minHeight: 0,
                      ...(active
                        ? {
                            backgroundColor: theme.palette.primary.light,
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              left: 18,
                              right: hasChildren ? 8 : 18,
                              bottom: 8,
                              height: 3,
                              borderRadius: 2,
                              background:
                                `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.light})`,
                              opacity: 0.85,
                            },
                          }
                        : {
                            "&:hover": { backgroundColor: "rgba(255,255,255,0.10)" },
                          }),
                    }}
                  >
                    {renderIcon(m.icon, isSmDown ? 22 : 26)}
                    <span>{m.label}</span>
                  </Button>

                  {hasChildren && (
                    <React.Fragment>
                      <Button
                        variant={active ? "contained" : "text"}
                        color={active ? "primary" : "inherit"}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (open) closeMenu(m.key);
                          else openMenu(m.key, e.currentTarget);
                        }}
                        sx={{
                          color: theme.palette.common.white,
                          px: 1,
                          py: 1.7,
                          minWidth: 46,
                          borderRadius: "0 999px 999px 0",
                          ml: "-6px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          ...(active
                            ? { backgroundColor: theme.palette.primary.light }
                            : { "&:hover": { backgroundColor: "rgba(255,255,255,0.10)" } }),
                        }}
                      >
                        <ChevronIcon
                          sx={{
                            fontSize: isSmDown ? 20 : 24,
                            transform: open ? "rotate(180deg)" : "rotate(0deg)",
                            transition: "transform 0.2s ease",
                          }}
                        />
                      </Button>

                      <Menu
                        anchorEl={anchorEls[m.key]}
                        open={open}
                        onClose={() => closeMenu(m.key)}
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                        transformOrigin={{ vertical: "top", horizontal: "left" }}
                        PaperProps={{
                          sx: {
                            mt: 1,
                            minWidth: 260,
                            borderRadius: 2,
                            boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
                          },
                        }}
                      >
                        {[
                          ...(m.to
                            ? [
                                <MenuItem
                                  key={`${m.key}-home`}
                                  onClick={() => {
                                    closeMenu(m.key);
                                    go(m.to);
                                  }}
                                  sx={{ fontWeight: 900, fontSize: "1rem", py: 1.2 }}
                                >
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    {renderIcon(m.icon, 20)}
                                    <span>{m.label} Home</span>
                                  </Box>
                                </MenuItem>,
                                <Divider key={`${m.key}-div`} />,
                              ]
                            : []),
                          ...(m.children || []).map((c) => (
                            <MenuItem
                              key={`${m.key}-${c.label}`}
                              disabled={c.disabled}
                              onClick={() => {
                                closeMenu(m.key);
                                if (!c.disabled && !c.external) go(c.to);
                                if (c.external) window.open(c.to, "_blank", "noopener");
                              }}
                              sx={{ fontWeight: 800, fontSize: "0.98rem", py: 1.1 }}
                            >
                              {c.label}
                            </MenuItem>
                          )),
                        ]}
                      </Menu>
                    </React.Fragment>
                  )}
                </Box>
              );
            })}
          </Box>
          {/* Role badge and quick link */}
          <Box sx={{ display: { xs: 'none', md: 'inline-flex' }, alignItems: 'center', gap: 1.25, mr: 1 }}>
            <Chip size="small" color="secondary" label={(role || 'User')} />
            {(role === 'ADMIN' || role === 'MANAGER') && (
              <Button variant="outlined" size="small" onClick={() => navigate('/settings/users')} sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.6)' }}>
                Users
              </Button>
            )}
          </Box>
          {/* Spacer */}
          <Box sx={{ flex: 1 }} />

          {/* Small-screen hamburger */}
          <Box sx={{ display: { xs: "inline-flex", md: "none" }, alignItems: "center", gap: 1 }}>
            <IconButton
              color="inherit"
              aria-label="Open navigation"
              onClick={(e) => openSmallNav(e.currentTarget)}
              sx={{ color: theme.palette.common.white }}
            >
              <MenuIcon sx={{ fontSize: 28 }} />
            </IconButton>
            <ThemeToggle />
            <IconButton onClick={handleLogout} color="inherit" aria-label="Logout" sx={{ color: theme.palette.common.white }}>
              <LogoutIcon sx={{ fontSize: 24 }} />
            </IconButton>
          </Box>

          {/* Desktop logout */}
          <Box sx={{ display: { xs: "none", md: "inline-flex" }, alignItems: 'center' }}>
            <ThemeToggle />
            <IconButton onClick={handleLogout} color="inherit" aria-label="Logout" sx={{ color: theme.palette.common.white }}>
              <LogoutIcon sx={{ fontSize: 24 }} />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Small-screen nav with app-color background */}
      <Menu
        anchorEl={smallNavEl}
        open={Boolean(smallNavEl)}
        onClose={closeSmallNav}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{ sx: {
            mt: 1,
            minWidth: 300,
            borderRadius: 2,
            bgcolor: theme.palette.primary.main,
            color: theme.palette.common.white,
            border: "1px solid rgba(255,255,255,0.12)",
            boxShadow: "0 10px 30px rgba(0,0,0,0.45)",
            backdropFilter: "blur(6px)",
            "& .MuiDivider-root": { borderColor: "rgba(255,255,255,0.16)" },
            "& .MuiMenuItem-root": {
              "&:hover": { backgroundColor: "rgba(255,255,255,0.10)" },
            },
          },
        }}
      >
        {visibleMenus.flatMap((m, idx) => {
          const arr: React.ReactNode[] = [];
          if (m.to) {
            arr.push(
              <MenuItem
                key={`${m.key}-main`}
                onClick={() => {
                  closeSmallNav();
                  go(m.to);
                }}
                sx={{ fontWeight: 900, py: 1.15 }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {renderIcon(m.icon, 22)}
                  <span>{m.label}</span>
                </Box>
              </MenuItem>
            );
          }
          if (m.children?.length) {
            arr.push(
              <Divider key={`${m.key}-divider`} />,
              ...m.children.map((c) => (
                <MenuItem
                  key={`${m.key}-${c.label}`}
                  disabled={c.disabled}
                  onClick={() => {
                    closeSmallNav();
                    if (!c.disabled && !c.external) go(c.to);
                    if (c.external) window.open(c.to, "_blank", "noopener");
                  }}
                  sx={{ pl: 5, py: 1.05, fontWeight: 700 }}
                >
                  {c.label}
                </MenuItem>
              ))
            );
          }
          if (idx < menus.length - 1) arr.push(<Divider key={`${m.key}-end`} />);
          return arr;
        })}

      </Menu>
      {/* MAIN CONTENT (scrolls between fixed header & fixed footer) */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          px: 3,
          pt: `${NAVBAR_HEIGHT + 16}px`,
          pb: `${FOOTER_HEIGHT + 28}px`, // space for fixed footer
        }}
      >
        {/* Subscription expiry banner (7 days warning + past due) */}
        {(() => {
          const sub = shop?.subscription;
          if (!sub || hideSubBanner) return null;
          const days = daysUntil(sub.currentPeriodEnd);
          const status = String(sub.status || '').toLowerCase();
          const showWarning = typeof days === 'number' && days <= 7 && days >= 0;
          const isOverdue = typeof days === 'number' && days < 0;
          const isSuspended = status === 'suspended' || status === 'past_due';
          if (!showWarning && !isOverdue && !isSuspended) return null;
          const endFmt = sub.currentPeriodEnd ? new Date(sub.currentPeriodEnd).toLocaleDateString() : '';
          const tone = isSuspended || isOverdue ? 'error' : 'warning';
          const message = isSuspended
            ? `Your subscription is ${status}. Please renew to restore access.`
            : isOverdue
              ? `Your subscription expired on ${endFmt}. Please renew to continue.`
              : (status === 'trialing'
                  ? `Your trial ends in ${days} day${days === 1 ? '' : 's'} (on ${endFmt}).`
                  : `Your subscription expires in ${days} day${days === 1 ? '' : 's'} (on ${endFmt}).`);
          return (
            <Paper elevation={0} sx={{
              mb: 2,
              px: 2,
              py: 1.5,
              borderRadius: 2,
              border: (theme) => `1px solid ${theme.palette.divider}`,
              backgroundColor: (theme) => tone === 'error' ? theme.palette.error.light : theme.palette.warning.light,
              color: (theme) => theme.palette.getContrastText(tone === 'error' ? theme.palette.error.light : theme.palette.warning.light),
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2
            }}>
              <Box>
                <Typography sx={{ fontWeight: 800 }}>{message}</Typography>
                {sub.planCode && (
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>Plan: {sub.planCode}</Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Button size="small" variant="contained" color={tone === 'error' ? 'error' : 'warning'}
                  onClick={() => {
                    // If you later add a tenant-visible billing page, navigate there
                    // For now, direct to Settings -> General
                    navigate('/settings/general');
                  }}>
                  Manage Billing
                </Button>
                <Button size="small" onClick={dismissSubBanner} sx={{ color: 'inherit' }}>Dismiss</Button>
              </Box>
            </Paper>
          );
        })()}

        {error ? <Typography color="error">{error}</Typography> : children ?? null}
      </Box>

      {/* COMPACT FIXED FOOTER with links + seam */}
      <Paper
        component="footer"
        square
        elevation={0}
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: (theme) => theme.zIndex.appBar - 1,
          minHeight: FOOTER_HEIGHT,
          color: isDark ? theme.palette.common.white : theme.palette.text.primary,
          background: isDark
            ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 60%, ${theme.palette.background.paper} 100%)`
            : theme.palette.background.paper,
          borderTop: `1px solid ${theme.palette.divider}`,
          boxShadow: "0 -10px 24px rgba(0,0,0,0.3)",
          overflow: "hidden",

          // Seam fade into footer
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            right: 0,
            top: -28,
            height: 28,
            pointerEvents: "none",
            background:
              "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(15,23,42,0.24) 65%, rgba(15,23,42,0.55) 100%)",
          },
        }}
      >
        {/* (Optional) subtle watermark; remove this Box if you prefer ultra-minimal */}
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            right: { xs: "-14%", sm: "-10%" },
            bottom: { xs: "-38%", sm: "-48%" },
            opacity: 0.06,
            pointerEvents: "none",
            transform: "rotate(-8deg)",
          }}
        >
          <img
            src={repair360Logo}
            alt=""
            style={{ height: Math.round(FOOTER_HEIGHT * 1.7), filter: "grayscale(100%)" }}
          />
        </Box>

        <Box
          sx={{
            position: "relative",
            maxWidth: 1400,
            mx: "auto",
            px: 2,
            py: { xs: 1, sm: 1.1 },
            minHeight: FOOTER_HEIGHT,
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 2 },
            flexWrap: "wrap",                       // wraps gracefully on small screens
            justifyContent: "space-between",
          }}
        >
          {/* Left: Shop name (bold) + contact */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 2 },
              flexWrap: "wrap",
              minWidth: 220,
              "& .MuiSvgIcon-root": { fontSize: { xs: 18, sm: 20 } },
            }}
          >
            <Typography
              sx={{
                typography: { xs: "subtitle1", sm: "h6" },
                fontWeight: 900,
                letterSpacing: 0.3,
                lineHeight: 1.1,
              }}
            >
              {shop?.tenantName || "Your Shop Name"}
            </Typography>

            {shop?.tenantAddress && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, opacity: 0.95 }}>
                <LocationIcon />
                <Typography sx={{ typography: { xs: "caption", sm: "body2" } }}>
                  {shop.tenantAddress}
                </Typography>
              </Box>
            )}
            {shop?.tenantPhone && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, opacity: 0.95 }}>
                <PhoneIcon />
                <Typography sx={{ typography: { xs: "caption", sm: "body2" } }}>
                  {shop.tenantPhone}
                </Typography>
              </Box>
            )}
            {shop?.tenantEmail && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, opacity: 0.95 }}>
                <EmailIcon />
                <Typography sx={{ typography: { xs: "caption", sm: "body2" } }}>
                  {shop.tenantEmail}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Right: Footer links */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: 1, sm: 1.5 },
              flexWrap: "wrap",
              ml: "auto",
            }}
          >
            {footerLinks.map((l) => (
              <Button
                key={l.label}
                onClick={() => navigate(l.to)}
                startIcon={l.icon}
                sx={{
                  color: isDark ? "rgba(255,255,255,0.92)" : (theme) => theme.palette.text.secondary,
                  fontWeight: 800,
                  fontSize: { xs: "0.86rem", sm: "0.92rem" },
                  textTransform: "none",
                  px: 0.25,
                  minWidth: "auto",
                  "& .MuiButton-startIcon": { mr: 0.5 },
                  "&:hover": { textDecoration: "underline", backgroundColor: "transparent" },
                }}
              >
                {l.label}
              </Button>
            ))}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}











